/**
 * Acquisition (docs/plans/2026-10-11_acquisition.md, P2): a plant's tag list or data export, studied into points, each
 * proposed as what it is, where it belongs, whether to keep it and how often to read it; a person decides the rows and
 * applies them. The Go side is platform-core/internal/acquisition (migration 0221); the contract is api/openapi.yaml
 * (AcquisitionSource, AcquisitionStudy, AcquisitionDecisionsPut, AcquisitionApplyResult, AcquisitionProfiles).
 *
 *   GET  /v1/twin/acquisition/sources                      the org's sources, newest first, each with its latest study
 *   POST /v1/twin/acquisition/sources                      multipart {file, kind, station_hint?, name?} (org admins)
 *   GET  /v1/twin/acquisition/studies/{study_id}           the study, its points, its plan's rows and their decisions
 *   PUT  /v1/twin/acquisition/studies/{study_id}/decisions {decisions}: accept, edit or reject rows (org admins)
 *   POST /v1/twin/acquisition/studies/{study_id}/apply     write the accepted rows (org admins); idempotent
 *   GET  /v1/twin/acquisition/profiles                     the foundry pack's acquisition profiles (any member)
 *
 * Every read is gated as the sibling twin modules gate theirs: the live build flag, a signed-in session and a resolved
 * org. A study still being read (queued or profiling) is read again every 10 s, and so is the list while any of its
 * latest studies is, so a file uploaded here is seen through to its proposal without a reload. The list is read a page
 * at a time (the server's offset and total), and decisions go to the server in batches of the most it takes.
 */
import { useMemo } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { PartialSave, putInChunks } from "@/lib/acquisition/review";
import { ANALYSIS_JOBS_KEY } from "./analysis-jobs";
import { VOCABULARY_BINDINGS_KEY } from "./twin-vocabulary";

/** What a source is: an upload (a tag list or a data export), or later a probe window or a plant agent's new kind. */
export type AcquisitionSourceKind = "tag_list" | "data_export" | "probe_window" | "agent_kind";
/** What a person may upload. */
export type AcquisitionUploadKind = "tag_list" | "data_export";
export type AcquisitionStudyStatus = "queued" | "profiling" | "proposed" | "applied" | "failed";
/** Who wrote a study's plan: the server's rules, or (P3) the brain; null before one is written. */
export type AcquisitionPlanBy = "rules" | "brain" | null;
export type AcquisitionDecisionKind = "accept" | "edit" | "reject";

/** A source's stored file in import_blobs (never its bytes). */
export interface AcquisitionFile {
  id: string;
  filename: string;
  content_type: string;
  byte_size: number;
  sha256: string;
  /** The bytes were purged; the metadata stays. */
  purged: boolean;
}

/** A study without its points and its plan. */
export interface AcquisitionStudySummary {
  id: string;
  status: AcquisitionStudyStatus;
  plan_by: AcquisitionPlanBy;
  /** The points the worker posted; 0 until the study is profiled. */
  point_count: number;
  /** Why the study failed, in the worker's words. */
  error?: string;
  /**
   * Why the plan is the rules' plan after all: the brain's plan was refused twice (its problems in words), its worker
   * gave the study up while it planned (the worker's words), or the brain did not finish in time. Omitted when empty.
   */
  plan_error?: string;
  created_at: string;
  updated_at: string;
  applied_at?: string;
}

/** One thing to study, with its file and its latest study. */
export interface AcquisitionSource {
  id: string;
  kind: AcquisitionSourceKind;
  name: string;
  /** The station the source belongs to, as the uploader said. */
  station_hint?: string;
  file?: AcquisitionFile;
  created_by?: string;
  created_at: string;
  latest_study?: AcquisitionStudySummary;
}

export interface AcquisitionSourceList {
  items: AcquisitionSource[];
  total: number;
  limit: number;
  offset: number;
}

export interface AcquisitionSourceCreated {
  source: AcquisitionSource;
  study: AcquisitionStudySummary;
}

/** One point of a source as the worker read it (a tag of a tag list, or a column of a data export). */
export interface AcquisitionPoint {
  /** Unique in the study. */
  key: string;
  name: string;
  path?: string;
  address?: string;
  data_type?: string;
  unit?: string;
  comment?: string;
  /** What the worker measured of it: finite numbers, booleans and short strings. */
  facts?: Record<string, number | boolean | string>;
}

/** How a point is read: exactly one of per cycle, on change, or every `running` seconds while running and `idle` idle. */
export interface AcquisitionRate {
  per_cycle?: boolean;
  on_event?: boolean;
  running?: number;
  idle?: number;
}

/** Where a point goes and how it is read (a row's proposal, or what a decision makes of it). */
export interface AcquisitionTarget {
  station_id?: string;
  entity_id?: string;
  /** The acquisition profile class. */
  class?: string;
  /** The class's signal it is. */
  signal?: string;
  /** The pack parameter it is (canonical name). */
  param?: string;
  unit?: string;
  scale?: number;
  keep: boolean;
  rate: AcquisitionRate;
  /** Never on a per-cycle point. */
  deadband?: number;
  /** Seconds after which an unchanged value is sent again; absent for a per-cycle record and a lab or a person's result. */
  heartbeat?: number;
  /** The point key of the counter whose step reads this per-cycle point. */
  trigger?: string;
}

/** A class a point may belong to, or what it may be, scored 0 to 1 with its evidence in words. */
export interface AcquisitionCandidate {
  id: string;
  label: string;
  score: number;
  evidence: string;
  class?: string;
  signal?: string;
  param?: string;
}

/** A required signal of a matched class that no point of the source maps to. */
export interface AcquisitionGap {
  class: string;
  class_label: string;
  signal: string;
  label: string;
  param?: string;
  words: string;
}

export interface AcquisitionPlanSummary {
  points: number;
  mapped: number;
  kept: number;
  dropped: number;
  /** Points with at least one problem. */
  problems: number;
  /** Rows proposed from an earlier applied study of the same layout. */
  learned: number;
  /** The classes the source matched, in the profiles' order. */
  classes: string[];
}

/** An edit's fields as a decision carries them (a null clears one). */
export interface AcquisitionEdit {
  station_id?: string | null;
  entity_id?: string | null;
  param?: string | null;
  unit?: string | null;
  scale?: number | null;
  keep?: boolean;
  rate?: AcquisitionRate;
  deadband?: number | null;
  /** 0 for none, or 60 to 604,800 seconds. */
  heartbeat?: number | null;
  trigger?: string | null;
}

/** One point's plan row, with the decision made on it and the target that decision makes. */
export interface AcquisitionReviewRow {
  point_key: string;
  name: string;
  proposal: AcquisitionTarget;
  /** The top candidate scored at least 0.6. */
  mapped: boolean;
  confidence: number;
  reason: string;
  keep_reason: string;
  /** The proposal is an earlier applied mapping of the same layout. */
  learned?: boolean;
  /** The brain's plan wrote the row's proposal, confidence and words over the rules row's (contract acquisition/v1). */
  by_brain?: boolean;
  classes: AcquisitionCandidate[];
  candidates: AcquisitionCandidate[];
  problems: string[];
  decision?: AcquisitionDecisionKind;
  /** An edit's fields, as stored (a cleared one null). */
  edited?: AcquisitionEdit;
  decided_at?: string;
  /** What the decision makes of the row: none for a rejected or undecided one. */
  target?: AcquisitionTarget;
}

export type AcquisitionApplyOutcome = "bound" | "mapped" | "rejected" | "conflict" | "refused" | "kept_person_decision";

/** What an apply wrote, kept with the study. */
export interface AcquisitionApplyResult {
  study_id: string;
  applied_at: string;
  /** This apply found the study applied, wrote nothing and answers what the first wrote. */
  already_applied: boolean;
  bindings_written: number;
  mappings_written: number;
  replaced: { name: string; was_param?: string; was_status: "proposed" | "approved"; param: string }[];
  skipped: {
    undecided: number;
    rejected: number;
    not_kept: number;
    no_param: number;
    conflict: number;
    refused: number;
    kept_person_decision: number;
  };
  rows: { point_key: string; name: string; outcome: AcquisitionApplyOutcome; param?: string; reason?: string }[];
}

/** A problem to fix on site, or a question for the plant, with the keys of the points it is about. */
export interface AcquisitionBrainNote {
  text: string;
  /** Point keys of the study. */
  points: string[];
}

/** A starting process window for a pack parameter, from the data: low to high, in the unit the server gives (if any). */
export interface AcquisitionBrainWindow {
  /** The pack parameter (canonical name). */
  param: string;
  low: number;
  high: number;
  unit?: string;
  why?: string;
}

/** The rows an hour the source sends as it is read today, and as the plan reads it. */
export interface AcquisitionBrainLoad {
  rows_per_hour_before: number;
  rows_per_hour_after: number;
}

/** What the brain's plan says beyond its rows (contract acquisition/v1); present when the brain wrote the plan. */
export interface AcquisitionBrainNotes {
  /** The model that wrote the plan. */
  model: string;
  /** For the person: what the source is, what to keep, the main problems. */
  summary: string;
  site_problems: AcquisitionBrainNote[];
  questions: AcquisitionBrainNote[];
  windows: AcquisitionBrainWindow[];
  load?: AcquisitionBrainLoad;
  /** The rows the brain wrote over the rules plan's. */
  rows_overridden: number;
}

export interface AcquisitionStudy {
  id: string;
  source: AcquisitionSource;
  status: AcquisitionStudyStatus;
  format: string;
  /** The layout's signature: a hash of the normalized header or tag list columns. */
  signature: string;
  plan_by: AcquisitionPlanBy;
  error?: string;
  /** Why the plan is the rules' plan after all (see AcquisitionStudySummary). Omitted when empty. */
  plan_error?: string;
  attempts: number;
  max_attempts: number;
  created_at: string;
  updated_at: string;
  applied_at?: string;
  applied_by?: string;
  points: AcquisitionPoint[];
  /** The rules that composed the plan (rules/2). */
  rules?: string;
  rows: AcquisitionReviewRow[];
  gaps: AcquisitionGap[];
  summary?: AcquisitionPlanSummary;
  /** The brain's notes, when the brain wrote the plan. */
  brain?: AcquisitionBrainNotes;
  applied_result?: AcquisitionApplyResult;
}

/** One decision as PUT .../decisions carries it: an edit carries its fields, accept and reject none. */
export type AcquisitionDecisionIn =
  | { point_key: string; decision: "accept" | "reject" }
  | { point_key: string; decision: "edit"; edited: AcquisitionEdit };

export interface AcquisitionDecisionsSaved {
  study_id: string;
  saved: number;
}

/** The role a signal is suggested on a machine's page; the plant accepts or edits it. */
export type AcquisitionSignalRole = "key_value" | "control" | "health" | "config" | "hidden";

/** A profile's signal: a pack parameter (`param`) or a machine signal. */
export interface AcquisitionProfileSignal {
  id: string;
  param?: string;
  /** The equipment port the signal lands on, where neither its param nor its id names it. */
  port?: string;
  label: string;
  /** The suggested role; roles key on the port (`port`, else `param`, else `id`). */
  role?: AcquisitionSignalRole;
  words: string[];
  need: "required" | "optional";
  kind: "analog" | "counter" | "flag" | "state" | "setpoint" | "text";
  unit?: string;
  source: "plc" | "instrument" | "scale" | "lab" | "person" | "derived";
  rate: AcquisitionRate;
  deadband?: number;
  heartbeat?: number;
  range?: [number, number];
  sentinels?: number[];
  defects?: string[];
  why: string;
}

/** One class of foundry equipment and what to collect from it. */
export interface AcquisitionProfileClass {
  id: string;
  label: string;
  templates: string[];
  words: string[];
  running?: { signal?: string; rule?: string };
  cycle?: { counter?: string; record?: string[]; settle?: number };
  identity: ("heat" | "ladle" | "mould" | "part" | "batch" | "sample" | "core" | "casting" | "lot")[];
  questions: string[];
  signals: AcquisitionProfileSignal[];
}

export interface AcquisitionProfiles {
  schema: "acquisition/v1";
  version: string;
  digest: string;
  classes: AcquisitionProfileClass[];
}

/** An upload: the file, its kind, and optionally the station it belongs to and a name (the file's name by default). */
export interface AcquisitionUpload {
  file: File;
  kind: AcquisitionUploadKind;
  station_hint?: string;
  name?: string;
}

const enc = encodeURIComponent;
const BASE = "/v1/twin/acquisition";

/** The upload's multipart form: `file`, `kind`, and `station_hint` and `name` only when given. */
export function acquisitionUploadForm(u: AcquisitionUpload): FormData {
  const fd = new FormData();
  fd.append("file", u.file, u.file.name);
  fd.append("kind", u.kind);
  if (u.station_hint) fd.append("station_hint", u.station_hint);
  const name = u.name?.trim();
  if (name) fd.append("name", name);
  return fd;
}

export const twinAcquisition = {
  sources: {
    list: (params: { limit?: number; offset?: number } = {}) => {
      const q = new URLSearchParams();
      q.set("limit", String(params.limit ?? 50));
      if (params.offset) q.set("offset", String(params.offset));
      return api.get<AcquisitionSourceList>(`${BASE}/sources?${q.toString()}`);
    },
    upload: (u: AcquisitionUpload) => api.postForm<AcquisitionSourceCreated>(`${BASE}/sources`, acquisitionUploadForm(u)),
  },
  studies: {
    get: (id: string) => api.get<AcquisitionStudy>(`${BASE}/studies/${enc(id)}`),
    decide: (id: string, decisions: AcquisitionDecisionIn[]) =>
      api.put<AcquisitionDecisionsSaved>(`${BASE}/studies/${enc(id)}/decisions`, { decisions }),
    apply: (id: string) => api.post<AcquisitionApplyResult>(`${BASE}/studies/${enc(id)}/apply`, {}),
  },
  profiles: () => api.get<AcquisitionProfiles>(`${BASE}/profiles`),
};

/** Build-time flag: only when explicitly "true" do live fetches fire. */
const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** Live fetches require the build flag AND an authenticated, org-resolved session. */
export function useAcquisitionLive(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

export const ACQUISITION_SOURCES_KEY = ["twin-acquisition", "sources"] as const;
export const ACQUISITION_PROFILES_KEY = ["twin-acquisition", "profiles"] as const;
export const acquisitionStudyKey = (id: string) => ["twin-acquisition", "study", id] as const;

/** A study the worker has not finished reading yet. */
export function acquisitionStudying(status: AcquisitionStudyStatus | undefined): boolean {
  return status === "queued" || status === "profiling";
}

/** The sources a page of the list asks for: the server's default page. */
export const SOURCES_PAGE = 50;

/** Where the next page of sources starts, or undefined where the last page reached the total (or came back empty). */
export function nextSourcesOffset(last: AcquisitionSourceList | undefined): number | undefined {
  if (!last || last.items.length === 0) return undefined;
  const next = last.offset + last.items.length;
  return next < last.total ? next : undefined;
}

/**
 * The sources of every page read so far, once each (a source uploaded since the first page shifts the later pages by
 * one, so the same source may close a page and open the next), and the org's total as the last page told it.
 */
export function sourcesOfPages(
  pages: readonly AcquisitionSourceList[] | undefined,
): { items: AcquisitionSource[]; total: number } | undefined {
  if (!pages || pages.length === 0) return undefined;
  const byId = new Map<string, AcquisitionSource>();
  for (const page of pages) for (const s of page.items) if (!byId.has(s.id)) byId.set(s.id, s);
  return { items: [...byId.values()], total: pages[pages.length - 1].total };
}

/**
 * The org's sources, newest first, a page at a time (`fetchNextPage` reads the next while `hasNextPage`); read again
 * every 10 s while any latest study is still being read.
 */
export function useAcquisitionSourcesQuery() {
  const q = useInfiniteQuery({
    queryKey: ACQUISITION_SOURCES_KEY,
    queryFn: ({ pageParam }) => twinAcquisition.sources.list({ limit: SOURCES_PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: nextSourcesOffset,
    enabled: useAcquisitionLive(),
    staleTime: 5_000,
    refetchInterval: (query) =>
      (sourcesOfPages(query.state.data?.pages)?.items ?? []).some((s) => acquisitionStudying(s.latest_study?.status))
        ? 10_000
        : false,
  });
  const data = useMemo(() => sourcesOfPages(q.data?.pages), [q.data]);
  return {
    data,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    hasNextPage: q.hasNextPage,
    fetchNextPage: q.fetchNextPage,
    isFetchingNextPage: q.isFetchingNextPage,
    isFetchNextPageError: q.isFetchNextPageError,
  };
}

/** One study; read again every 10 s while the worker is still reading it. */
export function useAcquisitionStudyQuery(id: string | undefined) {
  const live = useAcquisitionLive();
  return useQuery({
    queryKey: acquisitionStudyKey(id ?? ""),
    queryFn: () => twinAcquisition.studies.get(id as string),
    enabled: live && !!id,
    staleTime: 5_000,
    refetchInterval: (q) => (acquisitionStudying(q.state.data?.status) ? 10_000 : false),
  });
}

/**
 * A study read again now, into the cache: an acquisition_change card applied from the consultant reads the rows as they
 * stand, never as a page read them a while ago (lib/acquisition/consultant changeApplyPlan).
 */
export function useReadAcquisitionStudy(): (id: string) => Promise<AcquisitionStudy> {
  const qc = useQueryClient();
  return (id) =>
    qc.fetchQuery({ queryKey: acquisitionStudyKey(id), queryFn: () => twinAcquisition.studies.get(id), staleTime: 0 });
}

/** The pack's profiles: pack knowledge, the same for every org, read once a session. */
export function useAcquisitionProfilesQuery() {
  return useQuery({
    queryKey: ACQUISITION_PROFILES_KEY,
    queryFn: twinAcquisition.profiles,
    enabled: useAcquisitionLive(),
    staleTime: 30 * 60_000,
  });
}

export function useUploadAcquisitionSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (u: AcquisitionUpload) => twinAcquisition.sources.upload(u),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACQUISITION_SOURCES_KEY });
    },
  });
}

/**
 * Save decisions on a study's rows; the study is read again, so each row shows the target its decision makes. The server
 * takes at most MAX_DECISIONS in a PUT and refuses more whole, so a longer list goes in batches, one after the other: a
 * refusal of the first is the plain refusal, and a later one is a PartialSave that says how many are stored (the study
 * is read again then too).
 */
export function usePutAcquisitionDecisions(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (decisions: AcquisitionDecisionIn[]): Promise<AcquisitionDecisionsSaved> => ({
      study_id: studyId,
      saved: await putInChunks(decisions, (chunk) => twinAcquisition.studies.decide(studyId, chunk)),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: acquisitionStudyKey(studyId) });
    },
    onError: (err) => {
      if (err instanceof PartialSave) void qc.invalidateQueries({ queryKey: acquisitionStudyKey(studyId) });
    },
  });
}

/**
 * What an apply makes stale: the study (now applied), the sources list (its chip), the vocabulary bindings it wrote
 * and the analysis queue it started, as usePutVocabularyBindings reads them again for the same reasons.
 */
export function invalidateAfterApply(qc: Pick<QueryClient, "invalidateQueries">, studyId: string): void {
  for (const queryKey of [acquisitionStudyKey(studyId), ACQUISITION_SOURCES_KEY, VOCABULARY_BINDINGS_KEY, ANALYSIS_JOBS_KEY]) {
    void qc.invalidateQueries({ queryKey });
  }
}

/** Apply a study: it becomes applied, and the list's chip, the vocabulary and the analysis queue with it. */
export function useApplyAcquisitionStudy(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => twinAcquisition.studies.apply(studyId),
    onSuccess: () => invalidateAfterApply(qc, studyId),
  });
}
